import { mapPlayerProfile } from './player-profile.mappers';
import {
  PlayerProfileError,
  classifyPlayerProfileError,
} from './player-profile-api';
import type { ApiFetchPlayerProfilePort } from '~/handlers/ports/api-fetch-player-profile.port';
import type { PlayerProfile } from '~/types/player-profile';
import { getApostatudoApiUrl } from '~/utils/apostatudo/get-apostatudo-api-url';
import { getApostatudoAuthHeaders } from '~/utils/apostatudo/get-apostatudo-auth-headers';

// GET /players/me — elegibilidade por gamificação + nível VIP + nível CRM do
// jogador logado numa resposta só, pra o front resolver menu e badge sem
// encadear requests.
//
// A identidade É O TOKEN: o core introspecta o Bearer na Cactus
// (EnsureUserTokenIdentity) e resolve o jogador a partir dele. Nada vai na
// query — um `user_id` enviado é IGNORADO lá, e mandá-lo só sugeriria que o
// cliente escolhe de quem é o perfil. O `userId` recebido aqui serve só de
// fallback pro mapper, se a resposta vier sem `user_id`; a store, por sua vez,
// descarta a resposta se a conta trocou enquanto ela vinha.
//
// Custo do lado do core: a primeira chamada de cada token paga ~500ms de
// introspecção (veredito positivo cacheado 5 min); a PRIMEIRA de um jogador
// nunca visto ainda REGISTRA o jogador e faz o round trip até a view do cactus
// (0,45s a 2,4s). Daí o timeout de 10s, igual ao do board.
export class ApostatudoApiFetchPlayerProfileAdapter
  implements ApiFetchPlayerProfilePort
{
  async handle({ userId }: { userId: number }): Promise<PlayerProfile> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    let response: Response;

    try {
      response = await fetch(`${getApostatudoApiUrl()}/players/me`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...getApostatudoAuthHeaders(),
        },
        // Esta rota é pedida de novo a cada volta de foco e a cada abertura do
        // Clube Fidelidade, sempre no mesmo host: `keepalive` deixa o browser
        // reaproveitar a conexão em vez de pagar handshake a cada vez.
        keepalive: true,
        signal: controller.signal,
      });
    } catch {
      // Timeout (AbortError) ou queda de rede (TypeError).
      throw new PlayerProfileError('network');
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      // O corpo do erro é { error, message } nos dois casos do gate (401
      // `unauthenticated`, 503 `token_verification_unavailable`).
      const err = await response.json().catch(() => ({}));

      throw new PlayerProfileError(
        classifyPlayerProfileError(response.status, err?.error),
        err?.message,
      );
    }

    return mapPlayerProfile(await response.json(), userId);
  }
}
